import bcrypt from 'bcryptjs';
import { supabase } from '../config/supabase.js';
import { authService } from './auth.js';

const SALT_ROUNDS = 10;
const USER_FIELDS = 'id, email, full_name, is_active, last_login, created_at, updated_at';

const normalizeUserId = (id) => (id == null ? '' : String(id));

const formatSupabaseError = (error, fallback = 'An unexpected error occurred') => {
  if (!error) return fallback;
  if (error.code === 'PGRST116') {
    return 'User not found or update was not permitted';
  }
  if (error.code === '42501') {
    return 'You do not have permission to update this user';
  }
  return error.message || fallback;
};

const transformUser = (row) => {
  if (!row) return null;
  return {
    id: row.id,
    email: row.email,
    fullName: row.full_name || '',
    isActive: row.is_active !== false,
    lastLogin: row.last_login,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
};

export const isValidEmail = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

export const validatePassword = (password) => {
  if (!password || password.length < 6) {
    return 'Password must be at least 6 characters';
  }
  return null;
};

export const countActiveAdmins = async (excludeId = null) => {
  let query = supabase
    .from('admin_users')
    .select('id', { count: 'exact', head: true })
    .eq('is_active', true);

  if (excludeId) {
    query = query.neq('id', excludeId);
  }

  const { count, error } = await query;
  if (error) {
    throw new Error(formatSupabaseError(error, 'Failed to count active admins'));
  }
  return count || 0;
};

export const assertCanEditUserProfile = (userId) => {
  const currentUser = authService.getCurrentUser();
  if (currentUser?.userId !== userId) {
    throw new Error('You can only edit your own profile');
  }
};

export const getDeactivateBlockReason = (
  userId,
  isCurrentlyActive,
  { currentUserId = null, activeAdminCount = null } = {}
) => {
  if (!isCurrentlyActive) return null;

  if (
    currentUserId &&
    normalizeUserId(currentUserId) === normalizeUserId(userId)
  ) {
    return 'You cannot deactivate your own account';
  }

  if (activeAdminCount != null && activeAdminCount <= 1) {
    return 'Cannot deactivate the last active admin';
  }

  return null;
};

export const assertCanChangeActiveStatus = async (userId, nextIsActive) => {
  if (!nextIsActive) {
    const currentUser = authService.getCurrentUser();
    const blockReason = getDeactivateBlockReason(userId, true, {
      currentUserId: currentUser?.userId,
    });
    if (blockReason) {
      throw new Error(blockReason);
    }

    const otherActive = await countActiveAdmins(userId);
    if (otherActive === 0) {
      throw new Error('Cannot deactivate the last active admin');
    }
  }
};

export const listAdminUsers = async () => {
  const { data, error } = await supabase
    .from('admin_users')
    .select(USER_FIELDS)
    .order('created_at', { ascending: false });

  if (error) throw new Error(formatSupabaseError(error, 'Failed to load users'));
  return (data || []).map(transformUser);
};

export const getAdminUserById = async (id) => {
  const { data, error } = await supabase
    .from('admin_users')
    .select(USER_FIELDS)
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw new Error(formatSupabaseError(error, 'Failed to load user'));
  }
  return transformUser(data);
};

export const createAdminUser = async ({ email, fullName, password }) => {
  const normalizedEmail = email?.toLowerCase().trim();
  if (!normalizedEmail || !isValidEmail(normalizedEmail)) {
    throw new Error('A valid email address is required');
  }

  const passwordError = validatePassword(password);
  if (passwordError) throw new Error(passwordError);

  const passwordHash = await bcrypt.hash(password, SALT_ROUNDS);

  const { data, error } = await supabase
    .from('admin_users')
    .insert({
      email: normalizedEmail,
      full_name: fullName?.trim() || null,
      password_hash: passwordHash,
      is_active: true,
    })
    .select(USER_FIELDS)
    .single();

  if (error) {
    if (error.code === '23505') {
      throw new Error('A user with this email already exists');
    }
    throw new Error(formatSupabaseError(error, 'Failed to create user'));
  }

  return transformUser(data);
};

export const updateAdminUser = async (id, { email, fullName, isActive }) => {
  if (!id) throw new Error('User ID is required');

  const existing = await getAdminUserById(id);
  if (!existing) throw new Error('User not found');

  const payload = {};

  if (email !== undefined || fullName !== undefined) {
    assertCanEditUserProfile(id);
  }

  if (email !== undefined) {
    const normalizedEmail = email.toLowerCase().trim();
    if (!isValidEmail(normalizedEmail)) {
      throw new Error('A valid email address is required');
    }
    payload.email = normalizedEmail;
  }

  if (fullName !== undefined) {
    payload.full_name = fullName.trim() || null;
  }

  if (isActive !== undefined) {
    if (isActive !== existing.isActive) {
      await assertCanChangeActiveStatus(id, isActive);
    }
    payload.is_active = isActive;
  }

  if (Object.keys(payload).length === 0) {
    return existing;
  }

  const { data, error } = await supabase
    .from('admin_users')
    .update(payload)
    .eq('id', id)
    .select(USER_FIELDS)
    .single();

  if (error) {
    if (error.code === '23505') {
      throw new Error('A user with this email already exists');
    }
    throw new Error(formatSupabaseError(error, 'Failed to update user'));
  }

  if (!data) {
    throw new Error('User not found or update was not permitted');
  }

  return transformUser(data);
};

export const updateAdminUserPassword = async (id, password) => {
  if (!id) throw new Error('User ID is required');

  assertCanEditUserProfile(id);

  const passwordError = validatePassword(password);
  if (passwordError) throw new Error(passwordError);

  const passwordHash = await bcrypt.hash(password, SALT_ROUNDS);

  const { error } = await supabase
    .from('admin_users')
    .update({ password_hash: passwordHash })
    .eq('id', id);

  if (error) {
    throw new Error(formatSupabaseError(error, 'Failed to update password'));
  }
};

export const toggleAdminUserActive = async (id) => {
  if (!id) throw new Error('User ID is required');

  const user = await getAdminUserById(id);
  if (!user) throw new Error('User not found');

  const nextActive = !user.isActive;
  await assertCanChangeActiveStatus(id, nextActive);

  const { data, error } = await supabase
    .from('admin_users')
    .update({ is_active: nextActive })
    .eq('id', id)
    .select(USER_FIELDS)
    .single();

  if (error) {
    throw new Error(formatSupabaseError(error, 'Failed to update user status'));
  }

  if (!data) {
    throw new Error('User not found or update was not permitted');
  }

  return transformUser(data);
};
